import { FC } from 'react';
import { Grid } from '@mui/material';
import { FieldRow, FieldLabel, Panel, Title } from 'src/generic/component';
import { BooleanRadio, TextArea, Input } from 'src/generic/component/form';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { Draft } from 'src/types/common';
import { IAIRequest, IAITestPrep} from 'src/types/org/ai/schoolInterface';

interface ITestPrepPanelProps {
  item: Draft<IAIRequest>;
  readOnly?: boolean;
  errors: IFieldErrors;
  onUpdate: (updates: Draft<IAIRequest>) => void;
}

export const TestPrepPanel: FC<ITestPrepPanelProps> = ({
  item,
  readOnly,
  errors,
  onUpdate
}) => {
  const { getError, hasError } = ErrorManager(errors);

  const appForm = item.data?.appForm;
  const aiTestPrep = appForm?.aiTestPrep || {};

  const {
    hasTestPrep,
    description,
    schedule,
    onlyForEnrolledStudents,
    hasStudentFees,
    isOutsideCompany,
    hasInstructorCompensation,
    hasAccessToSecureMaterials
  } = aiTestPrep;

  const changedTestPrep = (updates: Partial<IAITestPrep>) => {
    const updatedItem: Draft<IAIRequest> = {
      ...item,
      data: {
        ...item.data,
        appForm: {
          ...appForm,
          aiTestPrep: {
            ...appForm?.aiTestPrep,
            ...updates
          }
        }
      }
    };
    onUpdate(updatedItem);
  };

  // Handle numeric input with validation
  const handleNumericInput = (value: string) => {
    // Only allow positive integers
    return value.replace(/[^0-9]/g, '');
  };

  return (
    <Panel>
      <Title>Exam Preparation Services</Title>
      <Grid container spacing={5}>
        {/* Left Side Grid - Test Prep Questions */}
        <Grid item xs={6}>
          {/* Does your organization offer test preparation services? */}
          <FieldRow>
            <FieldLabel variant="body1">
              Does your organization offer test preparation services?
            </FieldLabel>
            <BooleanRadio
              readOnly={readOnly}
              error={getError('data.appForm.aiTestPrep.hasTestPrep')}
              value={hasTestPrep}
              name='hasTestPrep'
              onChange={(value) => {
                const updates: Partial<IAITestPrep> = { hasTestPrep: value };
                // Clear dependent fields if No is selected
                if (!value) {
                  updates.description = undefined;
                  updates.schedule = undefined;
                  updates.onlyForEnrolledStudents = undefined;
                  updates.hasStudentFees = undefined;
                  updates.isOutsideCompany = undefined;
                  updates.hasInstructorCompensation = undefined;
                  updates.hasAccessToSecureMaterials = undefined;
                }
                changedTestPrep(updates);
              }}
            />
          </FieldRow>

          {/* Conditional Test Prep Questions - Only show if hasTestPrep is true */}
          {hasTestPrep && (
            <>
              {/* Description text area */}
              <FieldRow>
                <FieldLabel variant="body1">
                  Please provide a description of test preparation services offered
                </FieldLabel>
                <TextArea
                  disabled={readOnly}
                  validation={hasError('data.appForm.aiTestPrep.description') ? 'error' : undefined}
                  validationMsg={getError('data.appForm.aiTestPrep.description')}
                  onChange={(e) => {
                    changedTestPrep({ description: e.target.value });
                  }}
                  value={description || ''}
                />
              </FieldRow>

              {/* When are test prep classes held? */}
              <FieldRow>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <FieldLabel variant="body1">
                      When are test prep classes held?
                    </FieldLabel>
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      disabled={readOnly}
                      value={schedule || ''}
                      validation={hasError('data.appForm.aiTestPrep.schedule') ? 'error' : undefined}
                      validationMsg={getError('data.appForm.aiTestPrep.schedule')}
                      onChange={(e) => {
                        changedTestPrep({ schedule: e.target.value });
                      }}
                      maxLength={255}
                    />
                  </Grid>
                </Grid>
              </FieldRow>

              {/* Only for enrolled students? */}
              <FieldRow>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={8}>
                    <FieldLabel variant="body1">
                      Is the test preparation only for students enrolled in the organization submitting this code request?
                    </FieldLabel>
                  </Grid>
                  <Grid item xs={4}>
                    <BooleanRadio
                      readOnly={readOnly}
                      error={getError('data.appForm.aiTestPrep.onlyForEnrolledStudents')}
                      value={onlyForEnrolledStudents}
                      name='onlyForEnrolledStudents'
                      onChange={(value) => {
                        changedTestPrep({ onlyForEnrolledStudents: value });
                      }}
                    />
                  </Grid>
                </Grid>
              </FieldRow>
            </>
          )}
        </Grid>
        {/* Right Side Grid - Test Prep Questions (Conditional) */}
        <Grid item xs={6}>
          {hasTestPrep && (
            <>
              {/* Student fees? */}
              <FieldRow>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={8}>
                    <FieldLabel variant="body1">
                      Are students charged a fee for test preparation services?
                    </FieldLabel>
                  </Grid>
                  <Grid item xs={4}>
                    <BooleanRadio
                      readOnly={readOnly}
                      error={getError('data.appForm.aiTestPrep.hasStudentFees')}
                      value={hasStudentFees}
                      name='hasStudentFees'
                      onChange={(value) => {
                        changedTestPrep({ hasStudentFees: value });
                      }}
                    />
                  </Grid>
                </Grid>
              </FieldRow>

              {/* Outside company? */}
              <FieldRow style={{ marginTop: '24px' }}>
                <FieldLabel variant="body1">
                  Are test preparation services provided by an outside company or service?
                </FieldLabel>
                <BooleanRadio
                  readOnly={readOnly}
                  error={getError('data.appForm.aiTestPrep.isOutsideCompany')}
                  value={isOutsideCompany}
                  name='isOutsideCompany'
                  onChange={(value) => {
                    changedTestPrep({ isOutsideCompany: value });
                  }}
                />
              </FieldRow>

              {/* Instructor compensation? */}
              <FieldRow>
                <FieldLabel variant="body1">
                  Does the instructor involved in the College Board exam preparation receive compensation for these services outside of their normal salary?
                </FieldLabel>
                <BooleanRadio
                  readOnly={readOnly}
                  error={getError('data.appForm.aiTestPrep.hasInstructorCompensation')}
                  value={hasInstructorCompensation}
                  name='hasInstructorCompensation'
                  onChange={(value) => {
                    changedTestPrep({ hasInstructorCompensation: value });
                  }}
                />
              </FieldRow>

              {/* Access to secure materials? */}
              <FieldRow>
                <FieldLabel variant="body1">
                  Will any individuals involved with test preparation services have access to secure exam material storage?
                </FieldLabel>
                <BooleanRadio
                  readOnly={readOnly}
                  error={getError('data.appForm.aiTestPrep.hasAccessToSecureMaterials')}
                  value={hasAccessToSecureMaterials}
                  name='hasAccessToSecureMaterials'
                  onChange={(value) => {
                    changedTestPrep({ hasAccessToSecureMaterials: value });
                  }}
                />
              </FieldRow>
            </>
          )}
        </Grid>
      </Grid>
    </Panel>
  );
};